import clsx from "clsx";
import type { ReactNode } from "react";

import { ChatSequence } from "@/components/redesign/ChatSequence";
import { MarketingVisualBackdrop } from "@/components/redesign/MarketingVisualBackdrop";
import { WhatsAppDateLabel } from "@/components/redesign/WhatsAppDateLabel";

/* ────────────────────────── Shared phone chrome ────────────────────────── */

/**
 * Standalone Gail logomark (the "C" swoosh from the full wordmark).
 * Painted with `currentColor`, so set a text color where it's used.
 */
export function GailMark({ className }: Readonly<{ className?: string }>) {
  return (
    <svg
      viewBox="0 0 18 19"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M11.5469 6.31611V12.372L17.5811 6.31611V0.260193L11.5469 6.31611Z"
        fill="currentColor"
      />
      <path
        d="M5.51486 12.372C3.85025 10.7014 3.85025 7.98669 5.51486 6.3161C6.35057 5.47738 7.44212 5.05973 8.53366 5.05973V0.780533C6.35057 0.780533 4.16407 1.61925 2.49947 3.28985C-0.833156 6.63447 -0.833156 12.0571 2.49947 15.4051C5.83209 18.7497 11.2352 18.7497 14.5713 15.4051L11.5525 12.3754C9.88786 14.046 7.18288 14.046 5.51827 12.3754L5.51486 12.372Z"
        fill="currentColor"
      />
    </svg>
  );
}

function CellularIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg viewBox="0 0 16 11" className={className} fill="currentColor">
      <rect x="0" y="7" width="3" height="4" rx="0.7" />
      <rect x="4.3" y="4.8" width="3" height="6.2" rx="0.7" />
      <rect x="8.6" y="2.4" width="3" height="8.6" rx="0.7" />
      <rect x="13" y="0" width="3" height="11" rx="0.7" />
    </svg>
  );
}

function WifiIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg viewBox="0 0 16 11" className={className} fill="currentColor">
      <path d="M8 10.8c-.9 0-1.6-.7-1.6-1.6S7.1 7.6 8 7.6s1.6.7 1.6 1.6-.7 1.6-1.6 1.6Z" />
      <path d="M11.4 6.9 10.2 8a3.1 3.1 0 0 0-4.4 0L4.6 6.9a4.8 4.8 0 0 1 6.8 0Z" />
      <path d="M13.8 4.5l-1.2 1.2a6.5 6.5 0 0 0-9.2 0L2.2 4.5a8.2 8.2 0 0 1 11.6 0Z" />
      <path d="M16 2.3l-1.2 1.2a9.9 9.9 0 0 0-13.6 0L0 2.3a11.6 11.6 0 0 1 16 0Z" />
    </svg>
  );
}

function BatteryIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg viewBox="0 0 25 12" className={className}>
      <rect
        x="0.5"
        y="0.5"
        width="21"
        height="11"
        rx="3.2"
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.4"
      />
      <rect x="2" y="2" width="18" height="8" rx="1.8" fill="currentColor" />
      <path
        d="M23 4v4a2.2 2.2 0 0 0 0-4Z"
        fill="currentColor"
        fillOpacity="0.4"
      />
    </svg>
  );
}

function ChevronLeftIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg
      viewBox="0 0 12 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M10.5 1.5 2 10l8.5 8.5" />
    </svg>
  );
}

function VideoCameraIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg
      viewBox="0 0 25 17"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
      strokeLinecap="round"
      className={className}
    >
      <rect x="1" y="1.5" width="14.5" height="14" rx="3.8" />
      <path d="M18.7 6.7 22 4.3c.55-.4 1.3 0 1.3.66v7.08c0 .67-.75 1.05-1.3.66l-3.3-2.4" />
    </svg>
  );
}

function PhoneHandsetIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg viewBox="0 0 18 18" fill="currentColor" className={className}>
      <path d="M3.7.9 1.6 3A3.3 3.3 0 0 0 .8 6.3c.7 2.3 2.2 4.8 4.5 7.1s4.8 3.8 7.1 4.5a3.3 3.3 0 0 0 3.3-.8l2.1-2.1a1.5 1.5 0 0 0-.2-2.3l-2.9-2a1.5 1.5 0 0 0-1.9.2l-.9.9c-.4.4-1 .5-1.5.2a13.4 13.4 0 0 1-4.4-4.4c-.3-.5-.2-1.1.2-1.5l.9-.9a1.5 1.5 0 0 0 .2-1.9l-2-2.9A1.5 1.5 0 0 0 3.7.9Z" />
    </svg>
  );
}

/** Volume / action / power buttons machined into the titanium band. */
const SIDE_BUTTON =
  "absolute w-[5px] bg-gradient-to-b from-[#5d5e63] via-[#36373b] to-[#5d5e63]";

/**
 * An iPhone-style frame: titanium band with side buttons, black bezel,
 * dynamic island, and a status bar. The corner radii scale with the rendered
 * size so the phone keeps its shape across breakpoints.
 */
export function PhoneFrame({
  children,
  screenClassName,
  className,
}: Readonly<{
  children: ReactNode;
  /** Background classes for the screen area below the status bar. */
  screenClassName?: string;
  className?: string;
}>) {
  return (
    <div className={clsx("relative w-full", className)}>
      <span aria-hidden className={clsx(SIDE_BUTTON, "-left-[3px] top-[17.5%] h-[4%] rounded-l-[3px]")} />
      <span aria-hidden className={clsx(SIDE_BUTTON, "-left-[3px] top-[24.5%] h-[6.5%] rounded-l-[3px]")} />
      <span aria-hidden className={clsx(SIDE_BUTTON, "-left-[3px] top-[33%] h-[6.5%] rounded-l-[3px]")} />
      <span aria-hidden className={clsx(SIDE_BUTTON, "-right-[3px] top-[27%] h-[10.5%] rounded-r-[3px]")} />
      <div className="flex aspect-[495/1024] w-full flex-col rounded-[15.5%_/_7.5%] bg-gradient-to-b from-[#4d4e53] via-[#27282c] to-[#4d4e53] p-[1%] shadow-[0_2px_10px_rgba(10,15,30,0.35)]">
        <div className="flex min-h-0 flex-1 flex-col rounded-[14.8%_/_7.15%] bg-[#0a0a0c] p-[2.4%]">
          <div
            className={clsx(
              "relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-[12.2%_/_5.9%] [font-family:-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,'Helvetica_Neue',sans-serif]",
              screenClassName ?? "bg-white",
            )}
          >
            <div className="relative flex h-8 shrink-0 items-center justify-between bg-[#f7f7f8] px-[7%] text-black md:h-11">
              <span className="text-[0.6875rem] font-semibold tracking-tight md:text-[0.8125rem]">
                9:41
              </span>
              <span
                aria-hidden
                className="absolute left-1/2 top-1/2 aspect-[15/4] w-[27%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-black"
              >
                <span className="absolute right-[7%] top-1/2 aspect-square h-[48%] -translate-y-1/2 rounded-full bg-[radial-gradient(circle_at_35%_35%,#2a3548_0%,#10131c_60%)]" />
              </span>
              <span className="flex items-center gap-0.5 md:gap-1">
                <CellularIcon className="h-[7px] w-auto md:h-[10px]" />
                <WifiIcon className="h-[7px] w-auto md:h-[10px]" />
                <BatteryIcon className="h-[9px] w-auto md:h-[12px]" />
              </span>
            </div>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Replicates the layout of `MarketingFeatureImageVisual` with `fit="device"`,
 * but hosts live markup instead of an image: the phone is anchored at the top
 * of the card and its bottom crops inside the frame.
 */
export function MarketingFeaturePhoneVisual({
  label,
  children,
  className,
}: Readonly<{
  /** Accessible description of the conversation (replaces the old image alt). */
  label: string;
  children: ReactNode;
  className?: string;
}>) {
  return (
    <div
      className={clsx(
        "relative aspect-4/3 w-full overflow-hidden rounded-2xl md:aspect-square md:rounded-3xl",
        className,
      )}
    >
      <MarketingVisualBackdrop />
      <span className="sr-only">{label}</span>
      <div
        aria-hidden
        className="absolute left-1/2 top-5 w-[76%] -translate-x-1/2 drop-shadow-[0_24px_48px_-24px_rgba(15,40,80,0.45)] md:top-10 md:w-[68%]"
      >
        {children}
      </div>
    </div>
  );
}

/* ────────────────────────────── SMS (iMessage) ───────────────────────────── */

export type SmsConversationCopy = {
  contactName: string;
  contactInitials: string;
  customerOpener: string;
  gailIntro: string;
  customerAck: string;
  gailConfirmation: string;
  customerThanks: string;
  gailClose: string;
};

/* iMessage bubbles. The tail is built from two layers: `before` paints a
   bubble-colored block curving out past the corner, and `after` paints a
   screen-colored block on top whose rounded corner masks everything except
   the curl that remains visible. */
const SMS_BUBBLE =
  "relative max-w-[80%] rounded-[1.125rem] px-2.5 py-1 text-left text-[0.75rem] leading-snug before:absolute before:bottom-0 before:z-0 before:h-[0.875rem] before:w-[0.875rem] before:content-[''] after:absolute after:bottom-0 after:z-[1] after:h-[0.875rem] after:w-[7px] after:bg-white after:content-[''] md:px-3 md:py-1.5 md:text-[0.875rem]";
const SMS_OUTGOING = clsx(
  SMS_BUBBLE,
  "bg-[#34c759] text-white before:right-[-6px] before:rounded-bl-[12px] before:bg-[#34c759] after:right-[-7px] after:rounded-bl-[7px]",
);
const SMS_INCOMING = clsx(
  SMS_BUBBLE,
  "bg-[#e9e9eb] text-black before:left-[-6px] before:rounded-br-[12px] before:bg-[#e9e9eb] after:left-[-7px] after:rounded-br-[7px]",
);

/** Animated iMessage conversation inside an iPhone frame. */
export function SmsPhoneVisual({
  conversation,
}: Readonly<{ conversation: SmsConversationCopy }>) {
  return (
    <PhoneFrame>
      <div className="flex shrink-0 items-end justify-between border-b border-black/10 bg-[#f7f7f8] px-3.5 pb-1.5 md:pb-2">
        <ChevronLeftIcon className="mb-2.5 h-3.5 w-auto text-[#0a84ff] md:mb-4 md:h-[1.05rem]" />
        <div className="flex flex-col items-center gap-0.5 md:gap-1">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-b from-[#a9b6cf] to-[#8c9bb8] text-[0.6875rem] font-medium text-white md:h-10 md:w-10 md:text-[0.9375rem]">
            {conversation.contactInitials}
          </span>
          <span className="flex items-center gap-0.5 text-[0.625rem] text-black/70">
            {conversation.contactName}
            <svg
              viewBox="0 0 6 10"
              className="h-[7px] w-auto text-black/30"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            >
              <path d="m1 1 4 4-4 4" />
            </svg>
          </span>
        </div>
        <VideoCameraIcon className="mb-2.5 h-3.5 w-auto text-[#0a84ff] md:mb-4 md:h-4" />
      </div>
      <ChatSequence
        className="min-h-0 flex-1 space-y-2 overflow-hidden px-3 pt-2 md:pt-3"
        // Most of the thread is cropped by the card on mobile, so only a
        // small fraction of it can ever intersect.
        threshold={0.15}
        steps={[
          {
            align: "end",
            bubble: <p className={SMS_OUTGOING}>{conversation.customerOpener}</p>,
          },
          {
            align: "start",
            typing: true,
            typingClassName:
              "rounded-[1.125rem] bg-[#e9e9eb] text-black/70",
            bubble: <p className={SMS_INCOMING}>{conversation.gailIntro}</p>,
          },
          {
            align: "end",
            bubble: <p className={SMS_OUTGOING}>{conversation.customerAck}</p>,
          },
          {
            align: "start",
            typing: true,
            typingClassName:
              "rounded-[1.125rem] bg-[#e9e9eb] text-black/70",
            bubble: (
              <p className={SMS_INCOMING}>{conversation.gailConfirmation}</p>
            ),
          },
          {
            align: "end",
            bubble: <p className={SMS_OUTGOING}>{conversation.customerThanks}</p>,
          },
          {
            align: "start",
            typing: true,
            typingClassName:
              "rounded-[1.125rem] bg-[#e9e9eb] text-black/70",
            bubble: <p className={SMS_INCOMING}>{conversation.gailClose}</p>,
          },
        ]}
      />
    </PhoneFrame>
  );
}

/* ─────────────────────────────── WhatsApp ───────────────────────────────── */

export type WhatsAppConversationCopy = {
  contactName: string;
  contactStatus: string;
  customerQuestion: string;
  gailQuote: string;
  customerConfirm: string;
  /** The payment-link reply, split so the link itself can be styled. */
  gailLinkIntro: string;
  gailLinkText: string;
  gailLinkOutro: string;
};

const WA_OUTGOING =
  "max-w-[85%] rounded-lg rounded-tr-[0.2rem] bg-[#dcf8c6] px-2 py-1 text-left text-[0.75rem] leading-snug text-black/85 shadow-sm md:px-2.5 md:py-1.5 md:text-[0.875rem]";
const WA_INCOMING =
  "max-w-[85%] rounded-lg rounded-tl-[0.2rem] bg-white px-2 py-1 text-left text-[0.75rem] leading-snug text-black/85 shadow-sm md:px-2.5 md:py-1.5 md:text-[0.875rem]";

function WhatsAppMeta({
  time,
  read,
}: Readonly<{ time: string; read?: boolean }>) {
  return (
    <span className="ml-1.5 inline-flex translate-y-[0.3rem] items-center gap-0.5 whitespace-nowrap text-[0.5625rem] leading-none text-black/70">
      {time}
      {read && <span className="text-[#53bdeb]">✓✓</span>}
    </span>
  );
}

/** Animated WhatsApp conversation inside an iPhone frame. */
export function WhatsAppPhoneVisual({
  conversation,
  lang,
}: Readonly<{ conversation: WhatsAppConversationCopy; lang: string }>) {
  return (
    <PhoneFrame screenClassName="bg-[#ece5dd]">
      <div className="flex shrink-0 items-center gap-2 border-b border-black/10 bg-[#f7f7f8] px-2.5 py-1 md:py-1.5">
        <ChevronLeftIcon className="h-3 w-auto shrink-0 text-[#0a84ff] md:h-3.5" />
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-black/5 bg-white text-gail-primary md:h-8 md:w-8">
          <GailMark className="h-3.5 w-auto md:h-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[0.75rem] font-semibold text-black">
            {conversation.contactName}
          </p>
          <p className="truncate text-[0.5625rem] text-black/70">
            {conversation.contactStatus}
          </p>
        </div>
        <VideoCameraIcon className="h-3.5 w-auto shrink-0 text-[#0a84ff]" />
        <PhoneHandsetIcon className="h-3.5 w-auto shrink-0 text-[#0a84ff]" />
      </div>
      <div className="min-h-0 flex-1 space-y-2 overflow-hidden px-3 pt-2.5">
        <p className="text-center">
          <WhatsAppDateLabel lang={lang} />
        </p>
        <ChatSequence
          className="space-y-2"
          // See SmsPhoneVisual: the phone is cropped by the card on mobile.
          threshold={0.15}
          steps={[
            {
              align: "end",
              bubble: (
                <p className={WA_OUTGOING}>
                  {conversation.customerQuestion}
                  <WhatsAppMeta time="11:40" read />
                </p>
              ),
            },
            {
              align: "start",
              typing: true,
              typingClassName:
                "rounded-lg rounded-tl-[0.2rem] bg-white text-black/70 shadow-sm",
              bubble: (
                <p className={WA_INCOMING}>
                  {conversation.gailQuote}
                  <WhatsAppMeta time="11:40" />
                </p>
              ),
            },
            {
              align: "end",
              bubble: (
                <p className={WA_OUTGOING}>
                  {conversation.customerConfirm}
                  <WhatsAppMeta time="11:42" read />
                </p>
              ),
            },
            {
              align: "start",
              typing: true,
              typingClassName:
                "rounded-lg rounded-tl-[0.2rem] bg-white text-black/70 shadow-sm",
              bubble: (
                <p className={WA_INCOMING}>
                  {conversation.gailLinkIntro}
                  <span className="text-emerald-700 underline">
                    {conversation.gailLinkText}
                  </span>
                  {conversation.gailLinkOutro}
                  <WhatsAppMeta time="11:42" />
                </p>
              ),
            },
          ]}
        />
      </div>
    </PhoneFrame>
  );
}
